import * as amplitude from '@amplitude/analytics-browser'
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'

import { env } from '@/core/environments'

let initialized = false

/**
 * Detects whether the app is running inside Instagram's in-app browser on
 * Android. Instagram injects a Java object
 * (`navigationPerformanceLoggerJavascriptInterface`) into its Android WebView
 * to handle button-click metadata logging. If Amplitude's `elementInteractions`
 * autocapture fires while that Java object is being garbage-collected (a race
 * condition that only occurs on this specific combination of environment and
 * platform), the WebView throws "Java object is gone". Disabling
 * `elementInteractions` in this environment prevents the crash entirely.
 */
const isInstagramAndroidWebView = (): boolean => {
	const ua = navigator.userAgent
	return ua.includes('Instagram') && ua.includes('Android')
}

/**
 * Initialize Amplitude with EU region, full autocapture, and session replay.
 * Mirrors PostHog's no-op-when-key-missing behavior — safe to call even when
 * VITE_PUBLIC_AMPLITUDE_API_KEY is not set (localhost / CI with no secret).
 *
 * Call once at app startup, before any identify/track calls.
 */
export const initAmplitude = (): void => {
	if (initialized) return

	const apiKey = import.meta.env.VITE_PUBLIC_AMPLITUDE_API_KEY
	if (!apiKey) {
		return
	}

	if (env.ENABLE_AMPLITUDE === 'none') {
		return
	}

	// Session replay must be added before init()
	amplitude.add(sessionReplayPlugin({ sampleRate: 1 }))

	// Instagram's Android WebView injects a Java object for button-click
	// metadata logging that can be garbage-collected mid-flight. Amplitude's
	// elementInteractions autocapture triggers that interface on every click,
	// causing "Java object is gone" crashes. Disable it for that environment.
	const instagramAndroid = isInstagramAndroidWebView()

	amplitude.init(apiKey, {
		serverZone: 'EU',
		fetchRemoteConfig: true,
		autocapture: {
			attribution: true,
			fileDownloads: true,
			formInteractions: true,
			pageViews: true,
			sessions: true,
			elementInteractions: !instagramAndroid,
			// Network request tracking — required for Network Errors in Session Replay
			// (requires @amplitude/analytics-browser >= 2.24.0, we have 2.35.2)
			networkTracking: true,
			// Web Vitals (LCP, CLS, FID, etc.)
			webVitals: true,
			// Frustration signals visible in replay timeline
			// (requires @amplitude/analytics-browser >= 2.24.0)
			frustrationInteractions: {
				thrashedCursor: true,
				errorClicks: true,
				deadClicks: true,
				rageClicks: true,
			},
		},
	})

	initialized = true
}

/**
 * Identify the current user in Amplitude.
 * Maps the same identity fields that PostHogIdentify uses.
 */
export const identifyAmplitude = (
	userId: string,
	properties: Record<string, unknown>
): void => {
	const apiKey = import.meta.env.VITE_PUBLIC_AMPLITUDE_API_KEY
	if (!apiKey || !initialized) return

	amplitude.setUserId(userId)

	const identifyEvent = new amplitude.Identify()
	Object.entries(properties).forEach(([key, value]) => {
		if (value !== undefined && value !== null) {
			identifyEvent.set(key, value as amplitude.Types.ValidPropertyType)
		}
	})
	amplitude.identify(identifyEvent)
}

/**
 * Track an event in Amplitude.
 * Mirrors posthog.capture() call signature.
 */
export const trackAmplitude = (
	eventName: string,
	properties?: Record<string, unknown>
): void => {
	const apiKey = import.meta.env.VITE_PUBLIC_AMPLITUDE_API_KEY
	if (!apiKey || !initialized) return

	amplitude.track(eventName, properties)
}

/**
 * Reset Amplitude identity on logout.
 * Mirrors posthog.reset().
 */
export const resetAmplitude = (): void => {
	const apiKey = import.meta.env.VITE_PUBLIC_AMPLITUDE_API_KEY
	if (!apiKey || !initialized) return

	amplitude.reset()
}
